"use client";

import { useEffect, useRef, useState } from "react";

interface RevealProps {
  children: React.ReactNode;
  /** Stagger delay in milliseconds before the entrance animation starts. */
  delay?: number;
  className?: string;
}

/**
 * Scroll-triggered entrance wrapper. Plays the shared `cb-fade-up` keyframe
 * once its element scrolls into view, so below-the-fold content animates when
 * the user reaches it rather than on initial load. Honours prefers-reduced-motion
 * by rendering immediately with no animation.
 */
export function Reveal({ children, delay = 0, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // Reveal immediately, no animation. Deferred to the next frame so the
      // state update isn't synchronous within the effect body.
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={
        shown
          ? { animation: `cb-fade-up 0.5s ease-out ${delay}ms both` }
          : { opacity: 0 }
      }
    >
      {children}
    </div>
  );
}
